import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, Inject, Input, NO_ERRORS_SCHEMA } from "@angular/core";
import { HintboxOptions } from "@inscomponents/core";
import { L10n } from "@skylab-angular-shared/core";
import { L10nBase, ViolationWithMessage } from '@skylab-shared/core';
import { sum } from "@skylab-shared/utils";
import { Subject, takeUntil } from "rxjs";
import { HintboxDirective } from "../hintbox/hintbox.directive";
import { focusable } from "../utils/focusable";
import { ValidationGroupHintboxComponent } from "../validation/validation-group-hintbox/validation-group-hintbox.component";
import { ViewValidationViolationItem } from "./validate.directive";
import { ValidationIconComponent } from "./validation-icon/validation-icon.component";
import { ValidationSourceDirective } from "./validation-source.directive";

export interface ViolationWithMessageAction {
    key: string;
    action: () => void;
}

export interface ViolationWithMessageItem {
    key: string;
    message: string;
    action: Function;
    amount: number;
}

@Component({
    selector: 'skl-validation-group',
    templateUrl: './validation-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    imports: [ValidationIconComponent, HintboxDirective]
})
export class ValidationGroupComponent {
    @Input() public validationActions: () => ViolationWithMessageAction[] = () => [];

    public errors: ViolationWithMessage[] = [];
    public warnings: ViolationWithMessage[] = [];
    public infos: ViolationWithMessage[] = [];

    public warningsHintbox: HintboxOptions;
    public errorsHintbox: HintboxOptions;

    private destroy$ = new Subject<void>();
    private refresh$ = new Subject<void>();

    public constructor(
        private readonly validationSource: ValidationSourceDirective,
        @Inject(L10n) private readonly l10n: L10nBase,
        private readonly changeDetector: ChangeDetectorRef
    ) {
        this.warningsHintbox = {
            titleText: this.l10n._T('ui.validation-group.hintbox.warnings.title'),
            content: ValidationGroupHintboxComponent,
            contentProps: {
                validations: () => [...this.filterValidationDuplicates(this.warnings), ...this.filterValidationDuplicates(this.infos)]
            },
            placement: 'bottom-end'
        };

        this.errorsHintbox = {
            titleText: this.l10n._T('ui.validation-group.hintbox.errors.title'),
            content: ValidationGroupHintboxComponent,
            contentProps: {
                validations: () => this.filterValidationDuplicates(this.errors)
            },
            placement: 'bottom-end'
        };
    }

    public ngAfterViewInit(): void {
        this.refresh$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.changeDetector.detectChanges();
        });

        Array.from(this.validationSource.source.violationsValidations).forEach(e => this.parseViolation(e[0], e[1]));
        this.refresh$.next();

        this.validationSource.source.violationValidation$.pipe(takeUntil(this.destroy$)).subscribe(elem => {
            this.parseViolation(elem.key, elem.validation);
        });
    };

    private parseViolation(key: string, violation: ViewValidationViolationItem) {
        this.mapToView(key, violation, 'errors');
        this.mapToView(key, violation, 'warnings');
        this.mapToView(key, violation, 'infos', false);
        this.refresh$.next();
    }

    private mapToView(key: string, violation: ViewValidationViolationItem, violationKey: 'errors' | 'warnings' | 'infos', handleChanged = true): void {
        const collection = (this as any)[violationKey] as ViolationWithMessage[];
        const obj = collection.find(elem => elem.validationKey === key);
        if (obj) {
            obj.validationKey = key;
            obj.messages = [];
        } else {
            collection.push({messages: [], validationKey: key});
        }

        const newObj = collection.find(elem => elem.validationKey === key);
        if (newObj) {
            newObj.messages = handleChanged
                ? violation.changed ? violation[violationKey] as string[] : []
                : violation[violationKey] as string[];
        }

        this[violationKey] = collection.filter(elem => elem.messages?.length);
    }

    public filterValidationDuplicates(violations: ViolationWithMessage[]): ViolationWithMessageItem[] {
        const validationMessageItems: ViolationWithMessageItem[] = [];
        for (const violation of violations) {
            for (const message of violation.messages) {
                const validationItem = {
                    key: violation.validationKey,
                    message: message,
                    action: this.getValidationAction(violation.validationKey),
                    amount: 1
                };
                const duplicatedElement = validationMessageItems.find(item => item.message === validationItem.message);
                if (duplicatedElement) {
                    const index = validationMessageItems.indexOf(duplicatedElement);
                    validationMessageItems[index].amount++;
                } else {
                    validationMessageItems.push(validationItem);
                }
            }
        }
        return validationMessageItems;
    }

    private getValidationAction(key: string): Function {
        if (!this.validationActions) {
            return this.focusControlAction(key);
        }
        const validationAction = this.validationActions().find((item: ViolationWithMessageAction) => item.key === key);
        if (validationAction) {
            return validationAction.action;
        }
        return this.focusControlAction(key);
    }


    private focusControlAction(key: string): () => void {
        const focusControl = () => {
            const control = focusable(controlValidation);
            const insPanel = panel?.querySelector('ins-panel');
            if (insPanel) {
                (insPanel as any).open = true;
            }
            if (control.element) {
                (control.element as any)[control.focusMethodName]();
            }
        };

        const controlValidation = document.querySelector(`[validation-key="${key}"]`);
        const panel = controlValidation?.closest('[panel-id]');

        return focusControl;
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public get warningsCount(): number {
        return this.getMessagesCount(this.warnings) + this.getMessagesCount(this.infos);
    }

    public get errorsCount(): number {
        return this.getMessagesCount(this.errors);
    }

    private getMessagesCount(violations: ViolationWithMessage[]): number {
        return sum((violations || []), p => p.messages?.length ?? 0);
    }
}
